import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Recycle, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/lib/useSeo";

const values = [
  { icon: ShieldCheck, title: "Security first", body: "Every asset is treated as a live data risk until verifiably sanitized. No shortcuts, no exceptions." },
  { icon: Recycle, title: "True circularity", body: "Reuse before recycle, recycle before dispose. Zero-landfill is a baseline, not a marketing claim." },
  { icon: Target, title: "Radical documentation", body: "If it isn't documented, it didn't happen. Serialized chain of custody on every engagement." },
];

const timeline = [
  ["70+", "Countries served through our trusted global partner network."],
  ["1", "Single point of contact for complex, multinational IT lifecycle projects."],
  ["5", "Regional offices: USA, UK, Singapore, India and the UAE."],
  ["HQ", "Headquartered at Meydan Grandstand, Nad Al Sheba, Dubai, UAE."],
];

export default function About() {
  useSeo({
    title: "About ENVIROTEK LLC FZ — Certified ITAD in Dubai",
    description:
      "ENVIROTEK LLC FZ is a Dubai-based, certified IT Asset Disposition provider specializing in secure data destruction, decommissioning, recycling and remarketing across the GCC.",
    path: "/about",
  });

  return (
    <div className="bg-background min-h-screen">
      <Navbar />
      <main className="pt-28 md:pt-36" data-testid="about-page">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-8">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">/ About us</span>
              <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.86] mt-4">
                One partner.<br />Global reach.
              </h1>
            </div>
            <p className="lg:col-span-4 font-sans text-muted-foreground leading-relaxed">
              ENVIROTEK is a global provider of IT Asset Disposition, secure data destruction, data
              center decommissioning and technology lifecycle management — enabling organizations to
              execute complex projects worldwide under a single point of contact.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 border-t border-l border-border mb-24">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="border-r border-b border-border p-8 md:p-10"
              >
                <v.icon size={26} className="text-primary mb-6" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-2xl tracking-tight mb-3">{v.title}</h3>
                <p className="font-sans text-sm text-muted-foreground leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-12 gap-10 pb-24">
            <div className="lg:col-span-4">
              <h2 className="font-display font-extrabold uppercase tracking-tighter text-4xl md:text-5xl leading-[0.9]">Global reach</h2>
            </div>
            <div className="lg:col-span-8">
              {timeline.map(([year, text], i) => (
                <motion.div
                  key={year}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="grid grid-cols-12 gap-4 py-6 border-t border-border items-baseline"
                >
                  <span className="col-span-3 font-mono text-primary text-lg">{year}</span>
                  <p className="col-span-9 font-sans text-foreground/80">{text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <section className="bg-[#0a0a0a] text-white py-20 md:py-28">
          <div className="mx-auto max-w-[1500px] px-5 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <h2 className="font-display font-extrabold uppercase tracking-tighter text-4xl md:text-5xl leading-[0.9] max-w-xl">
              Ready to retire assets the right way?
            </h2>
            <Link to="/#quote" data-testid="about-cta" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-4 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors">
              Request a quote <ArrowUpRight size={16} />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
